// Browser auth against the Cognito Hosted UI using OAuth authorization-code + PKCE.
//
// The SPA client has no secret, so PKCE is the correct public-client flow:
//   1. login(): generate a code_verifier + code_challenge, stash the verifier, redirect to the
//      Hosted UI /authorize endpoint.
//   2. handleRedirectCallback(): on return (?code=...), exchange the code + verifier for tokens
//      at /oauth2/token, and cache them.
//   3. getIdToken(): return the cached id_token, refreshing via the refresh_token when expired.
//
// All ambient browser APIs (location, fetch, crypto, sessionStorage) are injected so the logic
// is testable under Vitest. This mirrors the spec's background-auth behavior (info-data/notes.md):
// an unauthenticated request bounces the user through the login endpoint and back.

import type { AtlasConfig, CryptoLike, FetchLike, LocationLike, StorageLike } from "./types";

const VERIFIER_KEY = "atlas.pkce_verifier";
const TOKENS_KEY = "atlas.tokens";
const CSRF_KEY = "atlas.csrf";
const REFRESH_SKEW_MS = 120_000;

interface CachedTokens {
  id_token?: string;
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  expiresAt: number;
}

// OAuth `state` carries the post-login return URL across the Cognito round-trip (+ a CSRF nonce).
// JSON -> base64url so it survives as a single query param.
export function encodeState(obj: unknown): string {
  return base64UrlEncode(new TextEncoder().encode(JSON.stringify(obj)));
}
export function decodeState(state: string): { return?: string; csrf?: string } | null {
  try {
    const bytes = Uint8Array.from(atobFn(state.replace(/-/g, "+").replace(/_/g, "/")), (c) =>
      c.charCodeAt(0),
    );
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
}

// A return URL is only honored if it points back into the configured app-domain family (or is a
// same-origin relative path) — blocks open-redirect via a forged `state`.
export function isSafeReturn(url: string | null | undefined, appDomain: string): boolean {
  if (!url) return false;
  if (url.startsWith("/")) return true; // same-origin relative
  try {
    const u = new URL(url);
    if (u.protocol !== "https:") return false;
    return u.hostname === appDomain || u.hostname.endsWith("." + appDomain);
  } catch {
    return false;
  }
}

// --- PKCE primitives (pure given a crypto impl) ---

export function base64UrlEncode(bytes: Uint8Array): string {
  let str = "";
  for (const b of bytes) str += String.fromCharCode(b);
  return btoaFn(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

const _btoa = typeof btoa === "function" ? btoa : null;
function btoaFn(s: string): string {
  if (_btoa) return _btoa(s);
  return Buffer.from(s, "binary").toString("base64"); // node fallback for tests
}

export function randomVerifier(crypto: CryptoLike, length = 64): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

export async function challengeFromVerifier(crypto: CryptoLike, verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(new Uint8Array(digest));
}

// --- token cache ---

export function decodeJwtClaims(idToken: string): Record<string, unknown> {
  const payload = idToken.split(".")[1];
  const json = atobFn(payload.replace(/-/g, "+").replace(/_/g, "/"));
  return JSON.parse(json);
}

const _atob = typeof atob === "function" ? atob : null;
function atobFn(s: string): string {
  if (_atob) return _atob(s);
  return Buffer.from(s, "base64").toString("binary");
}

function saveTokens(storage: StorageLike, tokens: Partial<CachedTokens>): void {
  // Compute an absolute expiry so refresh decisions don't depend on a clock at issue time.
  const expiresAt = nowMs() + (tokens.expires_in || 3600) * 1000;
  storage.setItem(TOKENS_KEY, JSON.stringify({ ...tokens, expiresAt }));
}

function loadTokens(storage: StorageLike): CachedTokens | null {
  const raw = storage.getItem(TOKENS_KEY);
  return raw ? JSON.parse(raw) : null;
}

let _now: () => number = () => Date.now();
export function _setNowForTests(fn: () => number): void {
  _now = fn;
}
function nowMs(): number {
  return _now();
}

// --- flow ---

export interface AuthDeps {
  config: AtlasConfig;
  location: LocationLike;
  fetch: FetchLike;
  crypto: CryptoLike;
  storage: StorageLike;
}

export class Auth {
  config: AtlasConfig;
  location: LocationLike;
  fetch: FetchLike;
  crypto: CryptoLike;
  storage: StorageLike;

  constructor(deps: AuthDeps) {
    this.config = deps.config;
    this.location = deps.location;
    this.fetch = deps.fetch;
    this.crypto = deps.crypto;
    this.storage = deps.storage;
  }

  isAuthenticated(): boolean {
    return Boolean(loadTokens(this.storage));
  }

  // Begin login.
  //
  // Subdomain mode, on an APP subdomain (not the auth origin): we can't complete OAuth here (Cognito
  // allowlists only the apex callback), so bounce to the apex auth origin carrying the return URL —
  // no PKCE locally. The apex then runs the real PKCE login() below.
  //
  // Otherwise (the apex auth origin, or path mode): standard PKCE — stash a verifier and redirect to
  // the Hosted UI. The return target (from a `?return=` the app bounce added) rides through Cognito
  // in the `state` param, CSRF-protected.
  async login(targetUrl?: string): Promise<void> {
    const c = this.config;
    if (c.routingMode === "subdomain" && !c.isAuthOrigin && c.authOrigin) {
      const ret = targetUrl || this.location.href;
      this.location.assign?.(`${c.authOrigin}?atlas_login=1&return=${encodeURIComponent(ret!)}`);
      return;
    }

    const verifier = randomVerifier(this.crypto);
    const challenge = await challengeFromVerifier(this.crypto, verifier);
    this.storage.setItem(VERIFIER_KEY, verifier);
    if (targetUrl) this.storage.setItem("atlas.target", targetUrl);

    const params = new URLSearchParams({
      response_type: "code",
      client_id: c.userPoolClientId,
      redirect_uri: c.redirectUri,
      scope: "openid email profile",
      code_challenge_method: "S256",
      code_challenge: challenge,
    });
    // Carry the post-login return target (set by the app→apex bounce) through Cognito in `state`,
    // bound by a CSRF nonce kept in this origin's sessionStorage.
    const ret = new URLSearchParams(this.location.search || "").get("return");
    if (ret) {
      const csrf = randomVerifier(this.crypto, 16);
      this.storage.setItem(CSRF_KEY, csrf);
      params.set("state", encodeState({ return: ret, csrf }));
    }
    this.location.assign?.(`${c.hostedUiDomain}/oauth2/authorize?${params}`);
  }

  // Detect a ?code= on the current URL.
  hasCallbackCode(search?: string): boolean {
    return new URLSearchParams(search || this.location.search).has("code");
  }

  // Exchange the authorization code for tokens. Returns the id_token.
  //
  // If the original login carried a return target (subdomain mode: an app bounced through the apex),
  // it round-trips in `state`. We validate the CSRF nonce + that the target is in the app-domain
  // family, then stash it as the post-login redirect target the bootstrap reads. A forged/foreign
  // `state` is ignored (falls back to the default landing), never followed.
  async handleRedirectCallback(search?: string): Promise<string> {
    const qs = new URLSearchParams(search || this.location.search);
    const code = qs.get("code");
    if (!code) throw new Error("no authorization code on the callback URL");
    const verifier = this.storage.getItem(VERIFIER_KEY);
    if (!verifier) throw new Error("missing PKCE verifier (login not started here?)");
    const tokens = await this._tokenRequest({
      grant_type: "authorization_code",
      code,
      redirect_uri: this.config.redirectUri,
      code_verifier: verifier,
    });
    this.storage.removeItem(VERIFIER_KEY);
    saveTokens(this.storage, tokens);

    const state = decodeState(qs.get("state") || "");
    const expectedCsrf = this.storage.getItem(CSRF_KEY);
    this.storage.removeItem(CSRF_KEY);
    if (
      state &&
      state.csrf &&
      expectedCsrf &&
      state.csrf === expectedCsrf &&
      isSafeReturn(state.return, this.config.appDomain)
    ) {
      this.storage.setItem("atlas.target", state.return!);
    }
    return tokens.id_token!;
  }

  // Return a valid id_token, refreshing if expired/near expiry. null if not logged in.
  async getIdToken(): Promise<string | null> {
    const tokens = loadTokens(this.storage);
    if (!tokens) return null;
    if (nowMs() < tokens.expiresAt - REFRESH_SKEW_MS) return tokens.id_token ?? null;
    if (!tokens.refresh_token) return null;
    const refreshed = await this._tokenRequest({
      grant_type: "refresh_token",
      refresh_token: tokens.refresh_token,
    });
    // Cognito does not re-issue the refresh token on refresh; keep the existing one.
    saveTokens(this.storage, { ...refreshed, refresh_token: tokens.refresh_token });
    return refreshed.id_token ?? null;
  }

  // Decode the cached ID token's claims (sub, cognito:groups, email, ...) WITHOUT a refresh or
  // network call — for role-aware UI (e.g. the Console hiding admin-only views). Returns null when
  // logged out or the token is unparseable. NOTE: this is a UX signal only; the ID token is not
  // verified here, so it must NEVER be the authorization boundary — the control-plane API validates
  // the JWT server-side. Uses the cached token as-is (may be expired); callers that need a fresh
  // token still go through getIdToken().
  getClaims(): Record<string, unknown> | null {
    const tokens = loadTokens(this.storage);
    if (!tokens || !tokens.id_token) return null;
    try {
      return decodeJwtClaims(tokens.id_token);
    } catch {
      return null;
    }
  }

  logout(): void {
    this.storage.removeItem(TOKENS_KEY);
    this.storage.removeItem(VERIFIER_KEY);
  }

  async _tokenRequest(fields: Record<string, string>): Promise<CachedTokens> {
    const body = new URLSearchParams({
      client_id: this.config.userPoolClientId,
      ...fields,
    });
    const resp = await this.fetch(`${this.config.hostedUiDomain}/oauth2/token`, {
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    });
    if (!resp.ok) {
      throw new Error(`token request failed: HTTP ${resp.status}`);
    }
    return resp.json();
  }
}
